import { useMutation, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useId, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, authApi, workspaceApi, type InvitePreview } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { useSession } from '../../app/session';
import { Alert, Button, Input, Spinner, buttonClasses } from '../../components/ui';
import { BrandGlyph, BrandWordmark } from '../../components/ui/brand';
import { Icon } from '../../components/icons';
import i18n from '../../i18n';
import { useCaptcha } from './turnstile';
import { PLAN_BLOCK_SHOWN_INLINE, planBlockOf, planBlockText } from '../billing/plan';

/**
 * The public pages (R45–R50, `Auth.dc.html`): a brand panel of at most 40%
 * in the sidebar's flat ink, with the French tagline naming Instagram and
 * Messenger, and the form on the right with 44px fields. A link that cannot
 * work (no token) says so and offers the one thing to do next.
 */

/** The sidebar's ink: the reply dot's knockout colour on the brand panel. */
const RAIL_SURFACE = 'var(--color-nav-bg)';

function AuthLayout({ title, lede, children, footer }: { title: string; lede: string; children: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(360px,2fr)_3fr]">
      {/* The brand, quiet and flat (A15-05): no glow, at most 40% of the width. */}
      <aside className="hidden flex-col justify-between gap-10 bg-nav-bg p-12 text-white lg:flex">
        <span className="flex items-center gap-2.5">
          {/* Charte §02 "sur fond prune": white bubble, dot knocked out in the panel's own colour. */}
          <BrandGlyph size={24} variant="on-dark" surface={RAIL_SURFACE} />
          <BrandWordmark className="text-lg text-white" />
        </span>
        <div className="flex max-w-[420px] flex-col gap-3.5">
          <p className="text-[34px] font-bold leading-[42px] tracking-[-0.02em]">{t('auth.rail.tagline')}</p>
          <p className="text-base leading-6 text-(--color-sidebar-text)">{t('auth.rail.body')}</p>
        </div>
        <p className="text-ds-sm text-(--color-sidebar-muted)">{t('auth.rail.footer')}</p>
      </aside>

      <main className="flex items-center justify-center bg-bg px-4 py-12">
        <div className="flex w-full max-w-[400px] flex-col gap-6">
          {/* On a phone the panel is gone: the lockup keeps the brand on the page. */}
          <span className="flex items-center gap-2.5 lg:hidden">
            <BrandGlyph size={24} surface="var(--color-bg)" />
            <BrandWordmark className="text-lg text-ink" />
          </span>
          <div className="flex flex-col gap-1.5">
            <h1 className="font-display text-[28px] leading-[34px] tracking-[-0.01em] text-ink">{title}</h1>
            <p className="text-ds-base text-ink-2">{lede}</p>
          </div>
          {children}
          {footer ? <div className="text-ds-sm text-ink-2">{footer}</div> : null}
        </div>
      </main>
    </div>
  );
}

/** A labelled 44px field; `aside` sits at the end of the label row ("Mot de passe oublié ?"), `hint` is tied to the input. */
function AuthField({ label, aside, hint, ...input }: { label: string; aside?: ReactNode; hint?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-ds-sm font-semibold text-ink">
          {label}
        </label>
        {aside}
      </div>
      <Input id={id} aria-describedby={hint ? hintId : undefined} className="h-11" {...input} />
      {hint ? (
        <span id={hintId} className="text-ds-xs text-ink-3">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

const LINK = 'font-semibold text-primary-700 hover:underline';

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link to="/login" className={`touch-target inline-flex items-center gap-1.5 self-start text-ds-sm ${LINK}`}>
      <Icon name="arrowLeft" className="size-3.5" />
      {t('auth.backToLogin')}
    </Link>
  );
}

/** The server's `CAPTCHA_*` refusals (D-160), in the user's language. */
const CAPTCHA_MESSAGES: Record<string, 'auth.captcha.required' | 'auth.captcha.failed' | 'auth.captcha.unavailable'> = {
  CAPTCHA_REQUIRED: 'auth.captcha.required',
  CAPTCHA_FAILED: 'auth.captcha.failed',
  CAPTCHA_UNAVAILABLE: 'auth.captcha.unavailable',
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const captcha = CAPTCHA_MESSAGES[err.code];
    return captcha ? i18n.t(captcha) : err.message;
  }
  return i18n.t('auth.genericError');
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Shown only once the server asks, after repeated failures from this client.
  const captcha = useCaptcha('login', false);

  const mutation = useMutation({
    mutationFn: () => authApi.login(email, password, captcha.input),
    onSuccess: async () => {
      await refresh();
      navigate('/', { replace: true });
    },
    onError: captcha.onError,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <AuthLayout
      title={t('auth.login.title')}
      lede={t('auth.login.lede')}
      footer={
        <>
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className={LINK}>
            {t('auth.login.register')}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {mutation.isError ? <Alert tone="err">{errorMessage(mutation.error)}</Alert> : null}
        <AuthField label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
        {/* Where people look for it (A15-04): beside the password label. */}
        <AuthField
          label={t('auth.password')}
          aside={
            <Link to="/forgot-password" className={`text-ds-sm ${LINK}`}>
              {t('auth.login.forgot')}
            </Link>
          }
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        {captcha.widget}
        <Button type="submit" size="lg" className="w-full" loading={mutation.isPending} disabled={mutation.isPending || captcha.pending}>
          {mutation.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const captcha = useCaptcha('register', true);
  const mutation = useMutation({
    mutationFn: () =>
      authApi.register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        ...(form.lastName ? { lastName: form.lastName } : {}),
        ...captcha.input,
      }),
    onError: captcha.onError,
  });

  if (mutation.isSuccess) {
    return (
      <AuthLayout title={t('auth.register.sentTitle')} lede={t('auth.register.sentLede', { email: form.email })}>
        <Alert tone="ok">
          {t('auth.register.sentBody')}{' '}
          <a className="underline" href="http://localhost:8025" target="_blank" rel="noreferrer">
            localhost:8025
          </a>
          .
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.register.title')}
      lede={t('auth.register.lede')}
      footer={
        <>
          {t('auth.register.already')}{' '}
          <Link to="/login" className={LINK}>
            {t('auth.register.login')}
          </Link>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="flex flex-col gap-4"
      >
        {mutation.isError ? <Alert tone="err">{errorMessage(mutation.error)}</Alert> : null}
        <div className="grid grid-cols-2 gap-3">
          <AuthField
            label={t('auth.register.firstName')}
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            autoFocus
            autoComplete="given-name"
          />
          <AuthField label={t('auth.register.lastName')} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} autoComplete="family-name" />
        </div>
        {/* "E-mail", not "Work email": creators use Gmail (A15-03). */}
        <AuthField label={t('auth.email')} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
        {/* The rule is read with the field (A15-06), not left floating under it. */}
        <AuthField
          label={t('auth.password')}
          hint={t('auth.passwordRule')}
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
          minLength={10}
          autoComplete="new-password"
        />
        {captcha.widget}
        <Button type="submit" size="lg" className="w-full" loading={mutation.isPending} disabled={mutation.isPending || captcha.pending}>
          {mutation.isPending ? t('auth.register.submitting') : t('auth.register.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** "Demander un nouvel e-mail" (R49): to the signed-in address, or to the one typed here. */
function ResendVerification() {
  const { t } = useTranslation();
  const { session } = useSession();
  const [typed, setTyped] = useState('');
  const email = session?.user.email ?? typed;
  const captcha = useCaptcha('resend-verification', true);
  const mutation = useMutation({
    mutationFn: () => authApi.resendVerification(email, captcha.input),
    onError: captcha.onError,
  });

  if (mutation.isSuccess) return <Alert tone="ok">{t('auth.verify.resent', { email })}</Alert>;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="flex flex-col gap-4"
    >
      {mutation.isError ? <Alert tone="err">{errorMessage(mutation.error)}</Alert> : null}
      {/* Without a session there is no address to send to: it is asked for. */}
      {session ? null : <AuthField label={t('auth.email')} type="email" value={typed} onChange={(e) => setTyped(e.target.value)} required autoComplete="email" />}
      {captcha.widget}
      <Button type="submit" size="lg" className="w-full" loading={mutation.isPending} disabled={mutation.isPending || captcha.pending}>
        {mutation.isPending ? t('auth.verify.requesting') : t('auth.verify.requestNew')}
      </Button>
    </form>
  );
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refresh } = useSession();

  const mutation = useMutation({
    mutationFn: () => authApi.verifyEmail(token),
    onSuccess: () => refresh(),
  });

  // Verification is an explicit click rather than an on-mount effect: email
  // clients and link scanners prefetch URLs, which would silently burn the
  // single-use token before the person ever saw the page.
  return (
    <AuthLayout title={t('auth.verify.title')} lede={t('auth.verify.lede')}>
      {!token ? (
        <div className="flex flex-col gap-4">
          <Alert tone="err" role="alert" icon="alert">
            <b className="font-semibold">{t('auth.verify.missingTitle')}</b> {t('auth.verify.missingBody')}
          </Alert>
          <ResendVerification />
          <BackToLogin />
        </div>
      ) : mutation.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Alert tone="ok">{t('auth.verify.done')}</Alert>
          <Link to="/" className={`touch-target w-full ${buttonClasses('primary', 'lg')}`}>
            {t('auth.verify.continue')}
          </Link>
        </div>
      ) : mutation.isError ? (
        // A link that was used or expired: the same way out as a missing one (R49).
        <div className="flex flex-col gap-4">
          <Alert tone="err" role="alert" icon="alert">
            {errorMessage(mutation.error)}
          </Alert>
          <ResendVerification />
          <BackToLogin />
        </div>
      ) : (
        <Button size="lg" className="w-full" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={mutation.isPending}>
          {mutation.isPending ? t('auth.verify.submitting') : t('auth.verify.submit')}
        </Button>
      )}
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const captcha = useCaptcha('password-forgot', true);
  const mutation = useMutation({
    mutationFn: () => authApi.forgotPassword(email, captcha.input),
    onError: captcha.onError,
  });

  return (
    <AuthLayout title={t('auth.forgot.title')} lede={t('auth.forgot.lede')}>
      {mutation.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Alert tone="ok">{t('auth.forgot.sent', { email })}</Alert>
          <BackToLogin />
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="flex flex-col gap-4"
        >
          {mutation.isError ? <Alert tone="err">{errorMessage(mutation.error)}</Alert> : null}
          <AuthField label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email" />
          {captcha.widget}
          <Button type="submit" size="lg" className="w-full" loading={mutation.isPending} disabled={mutation.isPending || captcha.pending}>
            {mutation.isPending ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
          </Button>
          {/* The only way back used to be the browser (R47). */}
          <BackToLogin />
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const mutation = useMutation({ mutationFn: () => authApi.resetPassword(token, password) });

  // No token, no form that can never be saved (A15-02, R48): say so and offer a new link.
  if (!token) {
    return (
      <AuthLayout title={t('auth.reset.title')} lede={t('auth.reset.lede')}>
        <div className="flex flex-col gap-4">
          <Alert tone="err" role="alert" icon="alert">
            <b className="font-semibold">{t('auth.reset.missingTitle')}</b> {t('auth.reset.missingBody')}
          </Alert>
          <Link to="/forgot-password" className={`touch-target w-full ${buttonClasses('primary', 'lg')}`}>
            {t('auth.reset.requestNew')}
          </Link>
          <BackToLogin />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset.title')} lede={t('auth.reset.lede')}>
      {mutation.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Alert tone="ok">{t('auth.reset.done')}</Alert>
          <Link to="/login" className={`touch-target w-full ${buttonClasses('primary', 'lg')}`}>
            {t('auth.reset.login')}
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="flex flex-col gap-4"
        >
          {mutation.isError ? (
            // An expired or used link: the server says why, and a new one is one click away.
            <Alert
              tone="err"
              action={
                <Link to="/forgot-password" className={`touch-target ${buttonClasses('secondary', 'sm')}`}>
                  {t('auth.reset.requestNew')}
                </Link>
              }
            >
              {errorMessage(mutation.error)}
            </Alert>
          ) : null}
          <AuthField
            label={t('auth.reset.newPassword')}
            hint={t('auth.passwordRule')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
            autoComplete="new-password"
            autoFocus
          />
          <Button type="submit" size="lg" className="w-full" loading={mutation.isPending} disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </Button>
          <BackToLogin />
        </form>
      )}
    </AuthLayout>
  );
}

/**
 * A 402 on accepting is the inviting workspace's plan (`maxUsers`, D-183):
 * said here, translated, with "ask the owner" — the invitee is not a member
 * of that workspace, so no "Change plan". Any other error reads as before.
 */
function inviteErrorMessage(err: unknown, t: TFunction): string {
  const block = planBlockOf(err);
  return block ? `${planBlockText(block, t).body} ${t('planBlock.askOwner')}` : errorMessage(err);
}

/**
 * "Atelier a atteint sa limite de 5 utilisateurs. Demandez à Nadia…" — the
 * inviting workspace and its owner by name (D-183), known from the preview
 * before the click. A 402 on accepting (a limit lowered after the page
 * loaded) says the same, with the limit the refusal carries.
 */
function fullMessage(preview: InvitePreview, error: unknown, t: TFunction): string | null {
  const block = planBlockOf(error);
  const refused = block?.kind === 'limit' && block.limitName === 'maxUsers';
  if (!preview.full && !refused) return null;
  const limit = (refused ? block.limit : null) ?? preview.limit;
  if (limit === null) return null;
  return t('auth.invite.full', {
    count: limit,
    limit: formatNumber(limit),
    workspaceName: preview.workspaceName,
    ownerName: preview.ownerName ?? t('auth.invite.theOwner'),
  });
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { session, isLoading, refresh } = useSession();
  const fullId = useId();

  // The inviting workspace, read before the click: a full one is said up front.
  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => workspaceApi.previewInvite(token),
    enabled: Boolean(session) && token !== '',
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => workspaceApi.acceptInvite(token),
    meta: PLAN_BLOCK_SHOWN_INLINE,
    onSuccess: async () => {
      await refresh();
      navigate('/', { replace: true });
    },
  });

  if (isLoading) {
    return (
      <AuthLayout title={t('auth.invite.title')} lede={t('auth.invite.ledeChecking')}>
        <Spinner />
      </AuthLayout>
    );
  }

  if (!session) {
    return (
      <AuthLayout title={t('auth.invite.title')} lede={t('auth.invite.ledeSignIn')}>
        {/* One primary action (R50). */}
        <Link to={`/login?next=${encodeURIComponent(`/accept-invite?token=${token}`)}`} className={`touch-target w-full ${buttonClasses('primary', 'lg')}`}>
          {t('auth.invite.signIn')}
        </Link>
      </AuthLayout>
    );
  }

  const full = preview.data ? fullMessage(preview.data, mutation.error, t) : null;
  // A preview refused (invalid, expired, another address) is what accepting would answer.
  const blocked = full !== null || preview.isError || (token !== '' && preview.isPending);

  return (
    <AuthLayout title={t('auth.invite.title')} lede={t('auth.invite.ledeAccepting', { email: session.user.email })}>
      <div className="flex flex-col gap-4">
        {full ? (
          <div id={fullId}>
            <Alert tone="warn">{full}</Alert>
          </div>
        ) : preview.isError ? (
          <Alert tone="err">{errorMessage(preview.error)}</Alert>
        ) : mutation.isError ? (
          <Alert tone="err">{inviteErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <Button
          size="lg"
          className="w-full"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={mutation.isPending || !token || blocked}
          disabledReason={token ? '' : t('auth.invite.missing')}
          aria-describedby={full ? fullId : undefined}
        >
          {mutation.isPending ? t('auth.invite.accepting') : t('auth.invite.accept')}
        </Button>
      </div>
    </AuthLayout>
  );
}
